'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import { useInstantMotion } from '../../use-instant-motion';

/**
 * CompleteBeat — the celebratory entrance "beat" shared by the four module-complete interstitials
 * (m1–m4 complete). Wraps the centered SealCheck + headline + sub-line column and gives it a
 * satisfying staggered entrance that faithfully replicates the vanilla prototype's module-complete
 * moment:
 *   1. the SealCheck "badge" springs in with a real back-out overshoot (scale 0.2 → 1), then
 *   2. an expanding accent ring "pings" out behind it and an 8-dot burst radiates from its center
 *      (decorative, layout-neutral overlays), with a soft success glow bloom, and
 *   3. the headline then sub-line rise + fade up just after the seal has landed.
 *
 * Timing/easing are ported 1:1 from the original CSS keyframes so the *feel* matches:
 *   - seal pop   = `mcBadgePop` 420ms cubic-bezier(0.34, 1.56, 0.64, 1), scale 0.2 → 1
 *   - ring ping  = `mcRing` 620ms ease-out, opacity 0.55 → 0, scale 0.7 → 2.1
 *   - dot burst  = `mcDot` 560ms cubic-bezier(0.22, 0.61, 0.36, 1), radiate ~54–64px then fade
 *   - text rise  = `mcRise` 360ms ease-out, translateY(10px) → 0, ~100ms apart
 *
 * Determinism contract: when `useInstantMotion()` is true (reduced-motion OR test automation —
 * Playwright `navigator.webdriver` / dev `__labsNoAuto`) it renders the children at their SETTLED
 * end-state with NO motion wrappers and NO ring/glow/burst, so the visual baselines for the
 * module-complete screens are unchanged. All variation is index-derived (no Math.random / Date.now).
 *
 * The children are passed as a flat list (icon, h1, p…) and laid out as
 * `flex-col items-center gap-4 text-center`, so the static visual is identical whether or not the
 * motion path runs.
 */

/** Original back-out spring used for the badge pop (`mcBadgePop`). */
const POP_EASE = [0.34, 1.56, 0.64, 1] as const;
/** Original ease-out used for ring + text rise (`mcRing` / `mcRise`). */
const OUT_EASE = [0.22, 0.61, 0.36, 1] as const;

/** 8-dot burst, ported from the original (`N = 8`, angle = i/N·2π + π/N, dist 54/64 alternating). */
const BURST_N = 8;
/** On-brand multi-color burst echoing the original green/gold/purple set, using v2 fs tokens. */
const BURST_COLORS = ['var(--fs-success)', 'var(--fs-warning)', 'var(--fs-brand)'] as const;
interface BurstDot {
  color: string;
  /** Final x offset from the seal center, px. */
  x: number;
  /** Final y offset from the seal center, px. */
  y: number;
}

/** Build the radiating dot set deterministically from the index (no RNG), matching the original. */
function buildBurst(): BurstDot[] {
  const out: BurstDot[] = [];
  for (let i = 0; i < BURST_N; i += 1) {
    const angle = Math.PI * 2 * i / BURST_N + Math.PI / BURST_N; // offset so none sit dead-axis
    const dist = 54 + (i % 2 === 0 ? 10 : 0); // slight ring jitter, as in the original
    out.push({
      x: Math.cos(angle) * dist,
      y: Math.sin(angle) * dist,
      color: BURST_COLORS[i % BURST_COLORS.length] ?? BURST_COLORS[0]
    });
  }
  return out;
}
export function CompleteBeat(t0) {
  const $ = _c(12);
  const {
    children
  } = t0;
  const instant = useInstantMotion();
  if (instant) {
    let t1;
    if ($[0] !== children) {
      t1 = <div className="flex w-full flex-col items-center gap-4 text-center">{children}</div>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      hidden: {},
      show: {
        transition: {
          staggerChildren: 0.1,
          delayChildren: 0.22
        }
      }
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children) {
    t2 = firstChild(children);
    $[3] = children;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <Beat pop={true}>{t2}</Beat>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children) {
    t4 = restChildren(children).map(_temp);
    $[7] = children;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <motion.div animate="show" className="flex w-full flex-col items-center gap-4 text-center" initial="hidden" variants={t1}>{t3}{t4}</motion.div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

/**
 * A single animated child slot. `pop` = the SealCheck (back-out spring scale-in + ring/glow/burst
 * bloom behind it); otherwise a short fade-up for the text lines.
 */
function _temp(child, i) {
  return <Beat key={i}>{child}</Beat>;
}
function Beat(t0) {
  const $ = _c(17);
  const {
    children,
    pop: t1
  } = t0;
  const pop = t1 === undefined ? false : t1;
  if (pop) {
    let T0;
    let t2;
    let t3;
    let t4;
    let t5;
    let t6;
    let t7;
    let t8;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      const burst = buildBurst();
      T0 = motion.div;
      t5 = "relative inline-flex items-center justify-center";
      t6 = {
        hidden: {
          opacity: 0,
          scale: 0.2
        },
        show: {
          opacity: 1,
          scale: 1,
          transition: {
            scale: {
              duration: 0.42,
              ease: POP_EASE
            },
            opacity: {
              duration: 0.18,
              ease: "easeOut"
            }
          }
        }
      };
      t7 = <motion.span aria-hidden={true} className="pointer-events-none absolute -z-10 size-[120px] rounded-full" style={{
        backgroundColor: "var(--fs-success)",
        filter: "blur(18px)"
      }} variants={{
        hidden: {
          opacity: 0,
          scale: 0.5
        },
        show: {
          opacity: [0, 0.22, 0],
          scale: [0.5, 1.4, 1.6],
          transition: {
            duration: 0.7,
            ease: "easeOut",
            delay: 0.08
          }
        }
      }} />;
      t8 = <motion.span aria-hidden={true} className="pointer-events-none absolute size-[88px] rounded-full border-2" style={{
        borderColor: "var(--fs-success)"
      }} variants={{
        hidden: {
          opacity: 0.55,
          scale: 0.7
        },
        show: {
          opacity: 0,
          scale: 2.1,
          transition: {
            duration: 0.62,
            ease: "easeOut",
            delay: 0.1
          }
        }
      }} />;
      t2 = true;
      t3 = "pointer-events-none absolute top-1/2 left-1/2 size-0";
      t4 = burst.map(_temp2);
      $[0] = T0;
      $[1] = t2;
      $[2] = t3;
      $[3] = t4;
      $[4] = t5;
      $[5] = t6;
      $[6] = t7;
      $[7] = t8;
    } else {
      T0 = $[0];
      t2 = $[1];
      t3 = $[2];
      t4 = $[3];
      t5 = $[4];
      t6 = $[5];
      t7 = $[6];
      t8 = $[7];
    }
    let t9;
    if ($[8] !== t4) {
      t9 = <span aria-hidden={t2} className={t3}>{t4}</span>;
      $[8] = t4;
      $[9] = t9;
    } else {
      t9 = $[9];
    }
    let t10;
    if ($[10] !== children || $[11] !== t9) {
      t10 = <T0 className={t5} variants={t6}>{t7}{t8}{t9}{children}</T0>;
      $[10] = children;
      $[11] = t9;
      $[12] = t10;
    } else {
      t10 = $[12];
    }
    return t10;
  }
  let t2;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 0,
      y: 10
    };
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      hidden: t2,
      show: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.36,
          ease: OUT_EASE
        }
      }
    };
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== children) {
    t4 = <motion.div variants={t3}>{children}</motion.div>;
    $[15] = children;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}

// --- child helpers (children is icon + h1 + p, a small flat list) -------------------------------
function _temp2(d, i) {
  return <motion.span className="absolute block rounded-full" key={i} style={{
    width: 9,
    height: 9,
    marginLeft: -4.5,
    marginTop: -4.5,
    backgroundColor: d.color,
    willChange: "transform, opacity"
  }} variants={{
    hidden: {
      opacity: 0,
      x: 0,
      y: 0,
      scale: 0.4
    },
    show: {
      opacity: [0, 1, 0],
      x: d.x,
      y: d.y,
      scale: 1,
      transition: {
        duration: 0.56,
        delay: 0.12,
        ease: OUT_EASE,
        opacity: {
          duration: 0.56,
          delay: 0.12,
          times: [0, 0.35, 1]
        }
      }
    }
  }} />;
}
function toArray(children: ReactNode): ReactNode[] {
  return Array.isArray(children) ? children : [children];
}
function firstChild(children: ReactNode): ReactNode {
  return toArray(children)[0];
}
function restChildren(children: ReactNode): ReactNode[] {
  return toArray(children).slice(1);
}